/**
 * ACTIVITY-CORE — human-readable feed text (i18n).
 *
 * App-SPECIFIC part (adapt when cloning): the entity_type+action →
 * phrase. The structure (patch, grouping, offline) lives in @listto/shared.
 * Message descriptors `msg` (not strings) are returned so the language is reactive.
 */
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { type ActivityRow, parseActivityPatch } from "@listto/shared";

interface ActivitySentence {
  /** Phrase with `{name}` interpolated. */
  named: MessageDescriptor;
  /** Phrase without a name (the patch does not contain one). */
  anonymous: MessageDescriptor;
}

/**
 * Fine semantic key: refines `updated`/`deleted` with what actually happened
 * happened (complete, move to trash…) by looking at the patch.
 */
export function activitySemanticKey(row: ActivityRow): string {
  const action = row.action ?? "updated";
  const patch = parseActivityPatch(row.patch);
  if (row.entity_type === "todo" && action === "updated" && patch.completed) {
    return patch.completed.new ? "completed" : "reopened";
  }
  if (action === "deleted") {
    // deleted_at transition = trash; full snapshot = permanent purge.
    return patch.deleted_at ? "trashed" : "purged";
  }
  return action;
}

const TODO_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`added the task "{name}"`, anonymous: msg`added a task` },
  completed: { named: msg`completed the task "{name}"`, anonymous: msg`completed a task` },
  reopened: { named: msg`reopened the task "{name}"`, anonymous: msg`reopened a task` },
  updated: { named: msg`edited the task "{name}"`, anonymous: msg`edited a task` },
  trashed: {
    named: msg`moved the task "{name}" to the trash`,
    anonymous: msg`moved a task to the trash`,
  },
  purged: {
    named: msg`permanently deleted the task "{name}"`,
    anonymous: msg`permanently deleted a task`,
  },
  restored: { named: msg`restored the task "{name}"`, anonymous: msg`restored a task` },
  reverted: {
    named: msg`undid a change on the task "{name}"`,
    anonymous: msg`undid a change on a task`,
  },
};

const LIST_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`created the list "{name}"`, anonymous: msg`created a list` },
  updated: { named: msg`edited the list "{name}"`, anonymous: msg`edited a list` },
  trashed: {
    named: msg`moved the list "{name}" to the trash`,
    anonymous: msg`moved a list to the trash`,
  },
  purged: {
    named: msg`permanently deleted the list "{name}"`,
    anonymous: msg`permanently deleted a list`,
  },
  restored: { named: msg`restored the list "{name}"`, anonymous: msg`restored a list` },
  reverted: {
    named: msg`undid a change on the list "{name}"`,
    anonymous: msg`undid a change on a list`,
  },
};

// Subtasks have no trash: their possible semantic keys are
// created / updated / purged (hard deletion) / reverted.
const SUBTASK_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`added the subtask "{name}"`, anonymous: msg`added a subtask` },
  updated: { named: msg`edited the subtask "{name}"`, anonymous: msg`edited a subtask` },
  purged: { named: msg`deleted the subtask "{name}"`, anonymous: msg`deleted a subtask` },
  reverted: {
    named: msg`undid a change on the subtask "{name}"`,
    anonymous: msg`undid a change on a subtask`,
  },
};

const FALLBACK: ActivitySentence = {
  named: msg`changed "{name}"`,
  anonymous: msg`made a change`,
};

/** Visible name of the entity extracted from the patch (text/name, new or old). */
export function activityEntityName(row: ActivityRow): string | null {
  const patch = parseActivityPatch(row.patch);
  for (const key of ["text", "name"] as const) {
    const change = patch[key];
    if (!change) {
      continue;
    }
    const value = change.new ?? change.old;
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
  }
  return null;
}

/**
 * i18n phrase for an entry: descriptor + values. Render with
 * `t(descriptor, values)` (or i18n._) in the component.
 */
export function activitySentence(row: ActivityRow): {
  descriptor: MessageDescriptor;
  values: Record<string, string>;
} {
  const key = activitySemanticKey(row);
  const table =
    row.entity_type === "todo"
      ? TODO_TEXTS
      : row.entity_type === "list"
        ? LIST_TEXTS
        : row.entity_type === "subtask"
          ? SUBTASK_TEXTS
          : {};
  const sentence = table[key] ?? FALLBACK;
  const name = activityEntityName(row);
  if (name) {
    return { descriptor: sentence.named, values: { name } };
  }
  return { descriptor: sentence.anonymous, values: {} };
}

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** "5 min ago" localized with Intl; falls back to day/date for older items. */
export function formatRelativeTime(
  iso: string | null,
  locale: string,
  now: Date = new Date(),
): string {
  if (!iso) {
    return "";
  }
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) {
    return "";
  }
  const diff = ts - now.getTime();
  const abs = Math.abs(diff);
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    if (abs < MINUTE_MS) {
      return rtf.format(Math.round(diff / SECOND_MS), "second");
    }
    if (abs < HOUR_MS) {
      return rtf.format(Math.round(diff / MINUTE_MS), "minute");
    }
    if (abs < DAY_MS) {
      return rtf.format(Math.round(diff / HOUR_MS), "hour");
    }
    if (abs < 7 * DAY_MS) {
      return rtf.format(Math.round(diff / DAY_MS), "day");
    }
    return new Date(ts).toLocaleDateString(locale);
  } catch {
    return new Date(ts).toLocaleString();
  }
}
